import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { 
  CalendarDaysIcon,
  PhotoIcon,
  CheckCircleIcon,
  ClockIcon,
  FlagIcon,
  HomeIcon,
  SparklesIcon,
  TruckIcon,
  PaintBrushIcon,
  WrenchScrewdriverIcon,
  CameraIcon,
  DocumentCheckIcon,
  StarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

// Default icon mapping for milestone types
const DEFAULT_MILESTONE_ICON = FlagIcon;

export default function CustomerGanttChart({ project, milestones = [], tasks = [], isAdmin = false, onMilestoneUpdate }) {
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [milestoneTypes, setMilestoneTypes] = useState([]);
  const chartRef = useRef(null);
  const scrollRef = useRef(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });
  const [zoomLevel, setZoomLevel] = useState(3); // Zoom level: 0.3 = zoomed out, 8 = zoomed in - default to 3x for detailed view

  useEffect(() => {
    loadMilestoneTypes();
  }, []);

  const loadMilestoneTypes = async () => {
    try {
      const settingsDoc = await getDocs(query(collection(db, 'settings'), where('type', '==', 'milestoneTypes')));
      if (!settingsDoc.empty) {
        const data = settingsDoc.docs[0].data();
        setMilestoneTypes(data.types || []);
      }
    } catch (error) {
      console.error('Error loading milestone types:', error);
    }
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (chartRef.current) {
        setChartDimensions({
          width: chartRef.current.offsetWidth,
          height: 400 // Reduced fixed height for the chart
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Helper function to safely convert any date format to Date object
  const convertToDate = (date) => {
    if (!date) return null;
    
    // Handle Firestore Timestamp (most common from Firestore)
    if (date && typeof date === 'object') {
      // Firestore Timestamp has toDate() method
      if (typeof date.toDate === 'function') {
        return date.toDate();
      }
      
      // Firestore timestamp with seconds and nanoseconds
      if (date.seconds !== undefined) {
        return new Date(date.seconds * 1000);
      }
      
      // Plain object with _seconds (sometimes from Firestore)
      if (date._seconds !== undefined) {
        return new Date(date._seconds * 1000);
      }
    }
    
    // Handle Date object
    if (date instanceof Date) {
      return new Date(date.getTime());
    }
    
    // Handle string date
    if (typeof date === 'string') {
      // If it's YYYY-MM-DD format, parse it properly to avoid timezone issues
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(date);
    }
    
    // Fallback - try to create a date from whatever we have
    try {
      return new Date(date);
    } catch (e) {
      console.error('Failed to convert date:', date, e);
      return null;
    }
  };
  
  // Calculate date range for the chart
  const getDateRange = () => {
    const allDates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    allDates.push(today); // Always include today
    
    // Add project dates
    if (project?.createdAt) {
      const date = convertToDate(project.createdAt);
      if (date) allDates.push(date);
    }
    if (project?.expectedEndDate) {
      const date = convertToDate(project.expectedEndDate);
      if (date) allDates.push(date);
    }
    
    // Add milestone dates
    console.log('Adding milestone dates, count:', milestones.length);
    milestones.forEach(m => {
      const date = convertToDate(m.date);
      if (date) {
        console.log('Milestone date converted:', m.title, date);
        allDates.push(date);
      }
    });
    
    // Add task dates
    tasks.forEach(t => {
      const startDate = convertToDate(t.startDate);
      const endDate = convertToDate(t.endDate);
      if (startDate) allDates.push(startDate);
      if (endDate) allDates.push(endDate);
    });

    console.log('All dates in array:', allDates.map(d => d.toDateString()));
    
    if (allDates.length === 0) {
      const now = new Date();
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 3, 0)
      };
    }

    let minDate = new Date(Math.min(...allDates));
    let maxDate = new Date(Math.max(...allDates));
    
    console.log('Min date before padding:', minDate.toDateString());
    console.log('Max date before padding:', maxDate.toDateString());
    
    // Calculate the range in days
    const rangeInDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
    
    // Always show at least 30 days, but use a focused view
    if (rangeInDays < 30) {
      // If range is too small, center around today with 30 days total
      const center = new Date(today);
      minDate = new Date(center);
      maxDate = new Date(center);
      minDate.setDate(minDate.getDate() - 10);
      maxDate.setDate(maxDate.getDate() + 20);
    } else {
      // For medium projects (30-90 days), show the full range with small padding
      minDate.setDate(minDate.getDate() - 3);
      maxDate.setDate(maxDate.getDate() + 7);
    }
    
    console.log('Date range calculated:', minDate, 'to', maxDate);
    
    return { start: minDate, end: maxDate };
  };

  const dateRange = useMemo(() => getDateRange(), [milestones, project, tasks]);
  const totalDays = useMemo(() => Math.ceil((dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24)), [dateRange]);
  
  // Calculate optimal chart width based on timeline length and zoom level
  const minWidth = 800;
  const baseDayWidth = Math.max(15, Math.min(40, (chartDimensions.width || 1000) / totalDays));
  const dayWidth = baseDayWidth * zoomLevel;
  const optimalWidth = Math.max(minWidth, totalDays * dayWidth);
  
  const getPositionForDate = (date) => {
    if (!date) return 0;
    
    const dateObj = convertToDate(date);
    if (!dateObj) return 0;
    
    // Normalize to start of day for consistent positioning
    dateObj.setHours(0, 0, 0, 0);
    const startDate = new Date(dateRange.start);
    startDate.setHours(0, 0, 0, 0);
    
    const daysSinceStart = Math.floor((dateObj - startDate) / (1000 * 60 * 60 * 24));
    const position = (daysSinceStart / totalDays) * 100;
    
    // Clamp position between 0 and 100
    return Math.max(0, Math.min(100, position));
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Generate month headers
  const generateMonthHeaders = () => {
    const headers = [];
    const currentDate = new Date(dateRange.start);
    
    while (currentDate <= dateRange.end) {
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const startPos = getPositionForDate(monthStart);
      const endPos = getPositionForDate(monthEnd);
      
      headers.push({
        month: monthStart.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
        startPos,
        width: endPos - startPos
      });
      
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return headers;
  };

  const monthHeaders = generateMonthHeaders();
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for accurate positioning
  const todayPosition = getPositionForDate(today);
  
  // Auto-scroll to today on mount (center on today)
  useEffect(() => {
    if (scrollRef.current) {
      // Always center on today position
      const todayPixelPosition = (todayPosition / 100) * optimalWidth;
      const scrollPosition = todayPixelPosition - (chartDimensions.width / 2);
      scrollRef.current.scrollLeft = Math.max(0, scrollPosition);
    }
  }, [optimalWidth, chartDimensions.width, todayPosition]);

  // Filter visible milestones
  const visibleMilestones = milestones.filter(m => m.visibleToCustomer !== false);
  
  // Debug: Log milestone dates to console
  useEffect(() => {
    console.log('=== GANTT CHART DEBUG ===');
    console.log('Date Range:', dateRange.start.toDateString(), 'to', dateRange.end.toDateString());
    console.log('Total Days:', totalDays);
    console.log('Today:', today.toDateString(), '-> Position:', todayPosition + '%');
    
    visibleMilestones.forEach(m => {
      console.log('---');
      console.log('Milestone:', m.title);
      console.log('Raw date value:', m.date);
      console.log('Date type:', typeof m.date, m.date?.constructor?.name);
      if (m.date?.seconds) {
        console.log('Has seconds property:', m.date.seconds);
      }
      const converted = convertToDate(m.date);
      console.log('Converted date:', converted?.toDateString());
      const pos = getPositionForDate(m.date);
      console.log('Position:', pos + '%');
    });
    console.log('=================');
  }, [visibleMilestones, dateRange, todayPosition, totalDays, today]);

  return (
    <div className="bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 rounded-3xl shadow-2xl border border-gradient-to-r border-purple-200 p-6 overflow-hidden relative h-[400px] flex flex-col">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-purple-100/40 to-transparent rounded-full -translate-y-32 translate-x-32 opacity-60"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-pink-100/40 to-transparent rounded-full translate-y-24 -translate-x-24 opacity-60"></div>
      
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="flex items-center">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-1 rounded-lg shadow-lg mr-2">
              <CalendarDaysIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Project Timeline
            </h2>
          </div>
          
          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoomLevel(Math.max(0.3, zoomLevel - 0.5))}
              className="p-1 bg-white hover:bg-purple-50 rounded-md shadow-sm border border-purple-200 transition-colors"
              title="Zoom Out"
            >
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <span className="text-xs text-gray-600 min-w-[3rem] text-center">{Math.round(zoomLevel * 100)}%</span>
            <button
              onClick={() => setZoomLevel(Math.min(8, zoomLevel + 0.5))}
              className="p-1 bg-white hover:bg-purple-50 rounded-md shadow-sm border border-purple-200 transition-colors"
              title="Zoom In"
            >
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              onClick={() => {
                // Calculate zoom level that fits the full timeline in view
                const fullTimelineZoom = Math.max(0.3, Math.min(2, (chartDimensions.width || 800) / (totalDays * 30)));
                setZoomLevel(fullTimelineZoom);
                // Scroll to beginning
                if (scrollRef.current) {
                  scrollRef.current.scrollLeft = 0;
                }
              }}
              className="p-1 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm transition-colors ml-2"
              title="Fit to View"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
            <button
              onClick={() => {
                if (scrollRef.current) {
                  const todayPixelPosition = (todayPosition / 100) * optimalWidth;
                  const scrollPosition = todayPixelPosition - (chartDimensions.width / 2);
                  scrollRef.current.scrollLeft = Math.max(0, scrollPosition);
                }
              }}
              className="p-1 bg-purple-500 hover:bg-purple-600 text-white rounded-md shadow-sm transition-colors"
              title="Go to Today"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>

      {/* Gantt Chart Container */}
      <div className="relative flex-1 min-h-0">
        {/* Scroll hint */}
        {optimalWidth > 800 && (
          <div className="absolute top-2 right-2 z-10 bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full shadow-sm animate-pulse">
            ← Scroll to explore timeline →
          </div>
        )}
        
        <div ref={scrollRef} className="overflow-x-auto scrollbar-thin scrollbar-thumb-purple-300 scrollbar-track-purple-100 hover:scrollbar-thumb-purple-400 transition-colors rounded-xl h-full">
          <div className="relative h-full" ref={chartRef} style={{ width: optimalWidth + 'px' }}>
        {/* Month Headers */}
        <div className="relative h-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100 shadow-inner mb-2">
          {monthHeaders.map((header, index) => (
            <div
              key={index}
              className="absolute top-0 h-full flex items-center justify-center border-r border-purple-200/50"
              style={{ left: `${header.startPos}%`, width: `${header.width}%` }}
            >
              <span className="text-xs font-bold text-purple-800 bg-white/80 px-2 py-0.5 rounded-full shadow-sm">
                {header.month}
              </span>
            </div>
          ))}
        </div>

        {/* Today Line */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-gradient-to-b from-red-400 via-red-500 to-red-400 z-20 shadow-lg rounded-full opacity-90"
          style={{ left: `${todayPosition}%` }}
        >
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs px-3 py-1.5 rounded-full shadow-lg font-semibold animate-pulse">
              Today
            </div>
            <div className="w-0 h-0 border-l-2 border-r-2 border-t-4 border-transparent border-t-red-500 mx-auto"></div>
          </div>
        </div>

        {/* Grid Lines */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(Math.ceil(totalDays / 7))].map((_, i) => {
            const weekPos = ((i * 7) / totalDays) * 100;
            return (
              <div
                key={i}
                className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-purple-100 to-transparent opacity-60"
                style={{ left: `${weekPos}%` }}
              />
            );
          })}
        </div>

        {/* Tasks Bars */}
        <div className="relative space-y-1 py-2 bg-white/40 backdrop-blur-sm rounded-lg border border-white/60 shadow-inner">
          {tasks.map((task, index) => {
            const startPos = getPositionForDate(task.startDate || task.createdAt);
            const endPos = getPositionForDate(task.endDate || task.expectedEndDate);
            const width = Math.max(endPos - startPos, 2); // Minimum width
            const isCompleted = task.status === 'completed';
            const isInProgress = task.status === 'in_progress';
            
            return (
              <div key={task.id} className="relative h-6 group">
                {/* Task bar */}
                <div
                  className={`absolute h-full rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl hover:scale-105 transform ${
                    isCompleted 
                      ? 'bg-gradient-to-r from-green-400 to-green-500 border-2 border-green-300 shadow-green-200' 
                      : isInProgress 
                      ? 'bg-gradient-to-r from-blue-400 to-blue-500 border-2 border-blue-300 shadow-blue-200 animate-pulse'
                      : 'bg-gradient-to-r from-gray-300 to-gray-400 border-2 border-gray-200'
                  }`}
                  style={{ left: `${startPos}%`, width: `${width}%` }}
                >
                  <div className="px-3 h-full flex items-center">
                    <span className="text-sm font-semibold text-white truncate drop-shadow-sm">
                      {task.name}
                    </span>
                    {isCompleted && (
                      <CheckCircleSolid className="w-5 h-5 text-white ml-2 flex-shrink-0 drop-shadow-sm" />
                    )}
                    {isInProgress && (
                      <ClockIcon className="w-5 h-5 text-white ml-2 flex-shrink-0 drop-shadow-sm" />
                    )}
                  </div>
                  
                  {/* Enhanced Tooltip */}
                  <div className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 hidden group-hover:block z-30">
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white text-xs rounded-xl px-4 py-3 whitespace-nowrap shadow-2xl border border-gray-600">
                      <div className="font-bold text-center mb-1">{task.name}</div>
                      <div className="text-center opacity-90">{formatShortDate(task.startDate)} - {formatShortDate(task.endDate)}</div>
                      <div className={`text-center capitalize font-semibold mt-1 ${
                        isCompleted ? 'text-green-300' : isInProgress ? 'text-blue-300' : 'text-yellow-300'
                      }`}>
                        {task.status || 'Pending'}
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Milestones */}
        <div className="relative h-12 mt-2 pt-2 border-t border-gradient-to-r border-purple-200 bg-gradient-to-r from-purple-50/30 via-pink-50/30 to-purple-50/30 rounded-lg shadow-inner">
          {visibleMilestones.map((milestone) => {
            const position = getPositionForDate(milestone.date);
            const convertedDate = convertToDate(milestone.date);
            const typeInfo = milestoneTypes.find(t => t.value === milestone.type);
            const IconComponent = DEFAULT_MILESTONE_ICON;
            const isCompleted = milestone.completed;
            const isPast = convertedDate < today;
            
            // Calculate pixel position for accurate placement
            const pixelPosition = (position / 100) * optimalWidth;
            
            return (
              <div
                key={milestone.id}
                className="absolute transform -translate-x-1/2 cursor-pointer group"
                style={{ 
                  left: `${pixelPosition}px`,
                  top: '0px'
                }}
                onClick={() => setSelectedMilestone(milestone)}
              >
                {/* Milestone marker */}
                <div 
                  className={`relative w-8 h-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-110 transform ${
                    isCompleted ? 'ring-1 ring-green-400 animate-pulse' : 'hover:ring-1 hover:ring-purple-300'
                  }`}
                  style={{ 
                    background: `linear-gradient(135deg, ${typeInfo?.color || '#6b7280'}, ${typeInfo?.color ? typeInfo.color + 'CC' : '#6b7280CC'})`,
                    borderWidth: '3px',
                    borderStyle: 'solid',
                    borderColor: 'white',
                    boxShadow: `0 8px 25px ${typeInfo?.color || '#6b7280'}33, 0 3px 10px ${typeInfo?.color || '#6b7280'}22`
                  }}
                >
                  <div className="w-full h-full flex items-center justify-center relative">
                    {typeInfo?.icon ? (
                      <span className="text-sm drop-shadow-lg filter">{typeInfo.icon}</span>
                    ) : (
                      <IconComponent className="w-3 h-3 text-white drop-shadow-lg" />
                    )}
                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 bg-white/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  {milestone.images && milestone.images.length > 0 && (
                    <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg border border-white">
                      <PhotoIcon className="w-2 h-2 text-white" />
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-3 h-3 flex items-center justify-center font-bold shadow-md text-xs">
                        {milestone.images.length}
                      </span>
                    </div>
                  )}
                  {isCompleted && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg border border-white">
                      <CheckCircleIcon className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Milestone label */}
                <div className="mt-1 text-center">
                  <div className="bg-white/90 backdrop-blur-sm px-1 py-0.5 rounded-sm shadow-sm border border-gray-100">
                    <div className="text-xs font-bold text-gray-800 whitespace-nowrap">
                      {milestone.title}
                    </div>
                    <div className="text-xs text-purple-600 font-medium">
                      {formatShortDate(milestone.date)}
                    </div>
                  </div>
                </div>
                
                {/* Hover tooltip */}
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 hidden group-hover:block z-30">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap">
                    <div className="font-semibold">{milestone.title}</div>
                    <div>{formatDate(milestone.date)}</div>
                    {milestone.description && <div className="text-gray-300">{milestone.description}</div>}
                    {milestone.images && milestone.images.length > 0 && (
                      <div className="text-blue-300">📷 {milestone.images.length} photo{milestone.images.length > 1 ? 's' : ''}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
          </div>
        </div>
      </div>

      {/* Enhanced Legend */}
      <div className="mt-2 pt-2 border-t border-gradient-to-r border-purple-200 flex-shrink-0">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-2 shadow-inner">
          <h3 className="text-xs font-bold text-gray-800 mb-1 flex items-center">
            <InformationCircleIcon className="w-3 h-3 mr-1 text-purple-600" />
            Legend
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            <div className="flex items-center p-1.5 bg-white rounded-md shadow-sm">
              <div className="w-4 h-4 bg-gradient-to-r from-green-400 to-green-500 border border-green-300 rounded-md mr-2 shadow-sm"></div>
              <span className="text-xs font-medium text-gray-700">Completed</span>
            </div>
            <div className="flex items-center p-1.5 bg-white rounded-md shadow-sm">
              <div className="w-4 h-4 bg-gradient-to-r from-blue-400 to-blue-500 border border-blue-300 rounded-md mr-2 shadow-sm animate-pulse"></div>
              <span className="text-xs font-medium text-gray-700">In Progress</span>
            </div>
            <div className="flex items-center p-1.5 bg-white rounded-md shadow-sm">
              <div className="w-4 h-4 bg-gradient-to-r from-gray-300 to-gray-400 border border-gray-200 rounded-md mr-2 shadow-sm"></div>
              <span className="text-xs font-medium text-gray-700">Pending</span>
            </div>
            <div className="flex items-center p-1.5 bg-white rounded-md shadow-sm">
              <div className="w-4 h-4 bg-gradient-to-r from-purple-500 to-purple-600 border border-white rounded-full mr-2 shadow-lg flex items-center justify-center">
                <FlagIcon className="w-2 h-2 text-white" />
              </div>
              <span className="text-xs font-medium text-gray-700">Milestone</span>
            </div>
            <div className="flex items-center p-1.5 bg-white rounded-md shadow-sm">
              <div className="w-1 h-4 bg-gradient-to-b from-red-400 to-red-500 rounded-full mr-2 shadow-sm animate-pulse"></div>
              <span className="text-xs font-medium text-gray-700">Today</span>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Milestone Detail Modal */}
      {selectedMilestone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMilestone(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  {(() => {
                    const typeInfo = milestoneTypes.find(t => t.value === selectedMilestone.type);
                    return (
                      <div 
                        className="w-12 h-12 rounded-full border-4 flex items-center justify-center mr-4"
                        style={{ 
                          backgroundColor: typeInfo?.color || '#6b7280',
                          borderColor: typeInfo?.color || '#6b7280'
                        }}
                      >
                        {typeInfo?.icon ? (
                          <span className="text-2xl">{typeInfo.icon}</span>
                        ) : (
                          <DEFAULT_MILESTONE_ICON className="w-6 h-6 text-white" />
                        )}
                      </div>
                    );
                  })()}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedMilestone.title}</h3>
                    <p className="text-gray-600">{formatDate(selectedMilestone.date)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMilestone(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {selectedMilestone.description && (
                <p className="text-gray-700 mb-6">{selectedMilestone.description}</p>
              )}
              
              {selectedMilestone.images && selectedMilestone.images.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Progress Photos</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedMilestone.images.map((image, index) => (
                      <div key={index} className="relative group cursor-pointer" onClick={() => setImagePreview(image)}>
                        <img
                          src={image.url}
                          alt={`${selectedMilestone.title} - Photo ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        {image.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 rounded-b-lg">
                            <p className="text-white text-sm">{image.caption}</p>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                          <CameraIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedMilestone.completed && (
                <div className="mt-6 flex items-center text-green-600">
                  <CheckCircleSolid className="w-5 h-5 mr-2" />
                  <span className="font-medium">Milestone Completed</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={() => setImagePreview(null)}>
          <div className="relative max-w-4xl w-full">
            <img
              src={imagePreview.url}
              alt="Preview"
              className="w-full h-auto rounded-lg"
            />
            {imagePreview.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 rounded-b-lg">
                <p className="text-white text-lg">{imagePreview.caption}</p>
              </div>
            )}
            <button
              onClick={() => setImagePreview(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}